import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	ConfirmModal,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import { IOption } from 'interfaces/select.interface'
import type { TagItem } from 'features/tags/types/tags.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchTags } from './queries/useFetchTags'
import { useCreateTagMutation } from './mutations/useCreateTagMutation'
import { useEditTagMutation } from './mutations/useEditTagMutation'
import { useTagDeleteMutation } from './mutations/useTagDeleteMutation'
import { useBulkDeleteMutation } from './mutations/useBulkDeleteMutation'
import { useExportBulkMutation } from './mutations/useExportBulkMutation'
import { TableSection } from './organisms/TableSection'

const TagsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedTags, setCheckedTags] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const [editedTag, setEditedTag] = useState<TagItem | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const { tags, isTagsLoading } = useFetchTags()
	const { select } = useTableSelect({ nodes: tags?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const [actionsFilter] = useActionFilters(!checkedTags.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()

	const { createTag, createTagLoading } = useCreateTagMutation({ onSuccess: closeAddDialog })

	const { editTag, editTagLoading } = useEditTagMutation({ onSuccess: () => setEditedTag(null) })

	const { tagDeleteMutation } = useTagDeleteMutation(idToBeRemoved as number, closeDeleteModal)

	const { bulkDeleteMutation } = useBulkDeleteMutation(checkedTags, () => {
		clearSelection()
		closeBulkDeleteModal()
	})

	const { exportBulkMutation, isExportLoading } = useExportBulkMutation(
		checkedTags,
		chosenExportFormat,
		() => {
			clearSelection()
			closeFormatModal()
			setChosenExportFormat(null)
		}
	)

	function onSelectChange(_action: any, state: any) {
		setCheckedTags(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedTag, onDelete: handleDelete })

	const handleAction = (action: string) => {
		switch (action) {
			case 'export':
				openExportModal()
				break
			case 'delete':
				openBulkDeleteModal()
				break
		}
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	const renderContent = () => {
		if (isTagsLoading) return <EmptyTable isLoading />
		if (!tags?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No tags yet')} />

		return <TableSection tags={tags} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Tags')}
						description={t('Tags that can be attached to products for filtering.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search tags')}
							placeholder={t('Search tags')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add tag')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add tag')}
					submitText={t('Create')}
					isLoading={createTagLoading}
					closeModal={closeAddDialog}
					onSubmit={createTag}
				/>
			)}
			{editedTag && (
				<NameDialog
					title={t('Edit tag')}
					submitText={t('Save')}
					defaultValue={editedTag.name}
					isLoading={editTagLoading}
					closeModal={() => setEditedTag(null)}
					onSubmit={(data) => editTag({ id: editedTag.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete tag')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={tagDeleteMutation}>
					<p>{t('You are about to remove a tag. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected tags')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={bulkDeleteMutation}>
					<p>
						{t('You are about to delete {{ count }} tags. Are you sure you want to proceed?', {
							count: checkedTags.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export tags')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>{t('You are about to export tags. Are you sure you want to proceed?')}</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={exportBulkMutation}
					isLoading={isExportLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default TagsPage
